import he from 'he'
import moment from 'moment'
import XHRPromise from 'xhr-promise'
import './vendor/History'
import Helpers from './Helpers'
import Locations from './results/Results.Location'
import Facets from './results/Results.Facets'
import Party from './results/Results.Party'
import Dates from './results/Results.Dates'
import UI from './results/Results.UI'
import Header from './results/Results.Header'
import Pagination from './results/Results.Pagination'
import Map from './results/Results.Map'
import Sorting from './results/Results.Sorting'
import Slider from '../modules/Slider'
import Tracking from './results/Results.Tracking'
import LastMinute from './results/Results.LastMinute'

const History = window.History

const searchControl = document.getElementsByClassName('results-aside')[0]

class Results {
    constructor() {

        this.resultsList = document.getElementsByClassName('results-list__items')[0]
        this.featuredSearchId = parseInt(document.getElementsByName('FeaturedSearchID')[0].value, 10)
        this.locations = new Locations(this.changeEvent, this.regionChangeEvent)
        this.facets = new Facets(this.changeEvent)
        this.party = new Party(this.changeEvent)
        this.dates = new Dates(this.changeEvent)
        this.pages = new Pagination(this.changeEvent)
        this.sorting = new Sorting(this.changeEvent)
        this.tracking = new Tracking()
        this.activeFetch = -1
        this.stateAction = ''
        this.map = new Map(() => {
            const qs = this.getNonDefaultValues()
            return this.buildQueryString(qs)
        })

        this.ui = new UI(this.showMoreEvent, this.extendEvent)
        this.lastMinute = new LastMinute(this)
        this.header = new Header()

        this.listeners()
        this.initState()

        const options = {
            threshold: 0.4
        }

        this.resultsItemObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting || entry.intersectionRatio > 0) {
                    const item = entry.target
                    let position = 0;
                    const resultsItems = document.querySelectorAll('.results-list__item')
                    let params = this.getNonDefaultValues()
                    let queryString = this.buildQueryString(params)
                    if (item.classList.contains('results-list__item') && item.attributes.getNamedItem('data-logged') === null) {
                        const cottageCode = item.getAttribute('data-code').substring(1)
                        //if the item in view is a complex, log that the complex collection has been seen and also the individual cottages of the complex
                        if (item.classList.contains('results-complex')) {
                            const complexCode = item.getAttribute('data-complexCode')
                            resultsItems.forEach((resultsItem, i) => {
                                if (resultsItem.getAttribute('data-complexCode') === item.getAttribute('data-complexCode')) {
                                    position = i + 1
                                }
                            })
                            let complexCottages = []
                            item.querySelectorAll('.slider__item').forEach((c) => {
                                complexCottages.push(c.getAttribute('data-code').substring(1))
                            })
                            let complexCodes = JSON.stringify(complexCottages)
                            let complex = { 'complexCode': complexCode, 'complexCodes': complexCodes, 'position': position, 'queryString': queryString, 'params': this.tracking.searchQsToJSON(params) }
                            this.tracking.trackComplexResultViewed(complex)
                            item.setAttribute('data-logged', 'true')
                        } else {
                            resultsItems.forEach((resultsItem, i) => {
                                if (resultsItem.getAttribute('data-code') === item.getAttribute('data-code')) {
                                    position = i + 1
                                }
                            })
                            ////log a cottage view when the results item has been seen
                            let cottage = { 'cottageCode': cottageCode, 'position': position, 'queryString': queryString, 'params': this.tracking.searchQsToJSON(params) }
                            this.tracking.trackResultsItemViewed(cottage)
                            item.setAttribute('data-logged', 'true')
                        }
                    }
                }
            })
        }, options)
    }

    listeners() {
        // control change events

        new Helpers(window).eventListener((e) => {
            const resultsItems = [].slice.call(document.querySelectorAll('.results-list__item'))
            resultsItems.forEach((item) => {
                this.resultsItemObserver.observe(item)
            })
        }, 'load')

        new Helpers(searchControl).eventListener((e) => {
            if (e.detail.control !== 'region' && e.detail.control !== 'page' && e.detail.control !== 'sorting') {
                this.featuredSearchId = 0
            }
            this.changeSearch(e.detail.control === 'page')
        }, 'search-change')

        new Helpers(searchControl).eventListener(() => {
            this.showMore()
        }, 'search-more')

        new Helpers(searchControl).eventListener((e) => {
            this.extendSearch(e.detail.params)
        }, 'search-extend')

        new Helpers(searchControl).eventListener(() => {
            this.updateState(this.currentUrl())
        }, 'search-map')

        // history navigation
        History.Adapter.bind(window, 'statechange', () => {
            const historyState = History.getState();
            const state = historyState.data;
            if (this.stateAction !== 'replace' && state !== undefined) {                
                this.updateControls(state);
                let options = this.lastMinute.valid ? { ...this.lastMinute.createUpdateSearchOptions(), url: historyState.url } : { ...this.setDefaultSearchOptions(), url: historyState.url };
                this.updateSearch(this.stateAction, options);
                if (this.lastMinute.valid) this.lastMinute.setSelectedDateRangeButton(this.lastMinute.lastMinuteButtons?.[state.lmbtn ?? 0]);
                // reset this flag
                this.stateAction = '';
            }
        })
    }

    // raise event to trigger search update - passed to child search control objects
    changeEvent(control) {
        // raise event to be picked up by the root change controller
        searchControl.dispatchEvent(new CustomEvent('search-change', { detail: { control } }))
        return this
    }

    showMoreEvent() {
        // raise event to be picked up by the root change controller
        searchControl.dispatchEvent(new CustomEvent('search-more'))
        return this
    }

    extendEvent(params) {
        searchControl.dispatchEvent(new CustomEvent('search-extend', {
            detail: { params },
        }))
        return this
    }

    // fetch all querystring params from independant controls
    getValues() {
        let values = Object.assign({},
            this.locations.getValues(),
            this.facets.getValues(),
            this.party.getValues(),
            this.dates.getValues(),
            this.pages.getValues(),
            this.map.getValues(),
            this.sorting.getValues(),

            {
                fsi: this.featuredSearchId,
                lmbtn: this.lastMinute.valid ? this.lastMinute.getCurrentIndexOfHighlightedButton() : null
            })

        return values
    }

    // fetch non-default querystring params from independant controls
    getNonDefaultValues() {
        const values = Object.assign({},
            this.locations.getNonDefaultValues(),
            //this.locations.getTownDefaultDistance(),
            this.facets.getNonDefaultValues(),
            this.party.getNonDefaultValues(),
            this.dates.getNonDefaultValues(),
            this.pages.getNonDefaultValues(),
            this.map.getNonDefaultValues(),
            this.sorting.getNonDefaultValues()
        )

        if (this.featuredSearchId !== 0) {
            values.fsi = this.featuredSearchId
        }

        return values
    }

    isCustomizedSearch() {
        const values = Object.assign({},
            this.facets.getNonDefaultValues(),
            this.party.getNonDefaultValues(),
            this.dates.getNonDefaultValues(),
            this.map.getNonDefaultValues(),
            this.sorting.getNonDefaultValues(),
        )

        const loc = this.locations.getNonDefaultValues()
        const pages = this.pages.getValues()

        const result = (Object.keys(values).length === 0 && (loc.nlc === undefined || loc.rgn === 'UK' || loc.rgn === '') && pages.pMax === pages.pMin && loc.nlm === undefined)
        return !result
    }

    // utility to turn search state parameters object into a querystring
    buildQueryString(values) {
        return Object
            .keys(values)
            .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(values[key])}`)
            .join('&')
    }

    // utility to return current search state parameters object from querystring
    readQueryString() {
        const searchParams = window.location.href.split('?')[1]
        const result = {}
        //in case the queryString is empty
        if (searchParams !== undefined) {
            const paramParts = searchParams.split('&')
            for (let part of paramParts) {
                const paramValuePair = part.split('=')
                //exclude the case when the param has no value
                if (paramValuePair.length === 2) {
                    result[paramValuePair[0]] = decodeURIComponent(paramValuePair[1])
                }
            }
        }
        return result
    }

    initState() {
        // replace current state with one that also contains the control state object
        const currentUrl = window.location.href.replace(window.location.protocol + '//' + window.location.hostname, '')
        const initialControlValues = this.getValues()

        // naff workaround for history.js - set flag to prevent ajax fetches
        // when we're just trying to modify current state
        this.stateAction = 'replace'
        History.replaceState(initialControlValues, document.title, currentUrl)
        this.stateAction = ''
        // ensure UI consistency by setting the controls from their read state
        this.updateControls(initialControlValues)
        new Helpers(window).eventListener((e) => {
            const resultsItems = [].slice.call(document.querySelectorAll('.results-list__item'))
            resultsItems.forEach((item) => {
                this.resultsItemObserver.observe(item)
            })
        }, 'load')

        // log cottage impressions of starting cottages
        Array.from(document.getElementsByClassName('results-list__item')).forEach((item, position) => {
            const cottageCode = item.getAttribute('data-code').substring(1)
            // track impression through GTM
            window.dataLayerQueue.push({ event: 'cottageImpression', cottageCode, position })
        })
    }

    // Push a new search state into history
    changeSearch(persistPagination = false) {
        // Reset pagination for a new search
        if (!persistPagination) {
            this.pages.setValues({})
        }
        let qs = this.getNonDefaultValues()
        const state = this.getValues()
        this.stateAction = 'push'
        History.pushState(state, document.title, this.currentUrl(qs))
        this.stateAction = ''

        // Store search dates for use on cottage availability form.
        try {
            const dates = {
                dss: moment(state.dss, 'ddd D MMM YY').toISOString(true),
                des: moment(state.des, 'ddd D MMM YY').toISOString(true),
                nday: state.nday,
            }
            sessionStorage.setItem('searchDates', JSON.stringify(dates))
        } catch (e) {
            console.log(e)
        }
    }

    currentUrl(nonDefaultParams = null) {
        //get params
        const qs = nonDefaultParams || this.getNonDefaultValues()
        // build a url appropriate for the current search
        let url = ''
        if ('fsi' in qs) {
            // featured search is set, therefore the search has yet to be modified
            // & a serverside built region url can be used
            url = this.locations.getRegionHref(this.pages.page())
            if ('sorting' in qs) {
                url += '?sorting=' + qs.sorting
            }
        } else if (!this.isCustomizedSearch()) {
            // not customised, excepting location. Use the location url
            url = this.locations.getLocationUrl(this.pages.page())
        } else {

            url = `/results.aspx?search=Y&${this.buildQueryString(qs)}`
        }

        return url
    }

    extendSearch(params) {
        // get current state
        const state = History.getState().data
        // change values
        Object.assign(state, params)
        // modify controls
        this.updateControls(state)

        // navigate to the top of the results list
        document.querySelector('.results-output').scrollIntoView({ behavior: 'smooth' })

        // update list
        this.changeSearch()
    }

    // without fetching new data, just update the state to reflect current control values
    updateState(url) {
        if (url === null) url = this.currentUrl();
        // naff workaround for history.js - set flag to prevent ajax fetches
        // when we're just trying to modify current state
        this.stateAction = 'replace'
        History.replaceState(this.getValues(), document.title, url)
        this.stateAction = ''
    }


    setDefaultSearchOptions = () => {
        return {
            feedEndpoint: this.getFeedEndpoint(),
            updateMetaTitle: true,
            preservePageDescription: false,
            preservePageHeader: false,
            url: this.currentUrl()
        };
    };

    getFeedEndpoint = () => {
        const nonDefaultValues = this.getNonDefaultValues(); // Define qs here
        let feedEndpoint = `/feeds/resultsFeedHtml.aspx?rpp=10&${this.buildQueryString(nonDefaultValues)}`;
        if (this.isCustomizedSearch()) {
            feedEndpoint += '&search=Y';
        }
        return feedEndpoint;
    };

    // Fetch results from feed with the current search state
    updateSearch(action, options) {
        options = options || this.setDefaultSearchOptions();
        const { feedEndpoint, updateMetaTitle, preservePageDescription, preservePageHeader, url } = options;
        const self = this;
        const qs = this.getNonDefaultValues();
        this.ui.loading();

        if (this.activeFetch !== -1) {
            this.activeFetch.getXHR().abort()
        }
        // ajax fetch new results
        this.activeFetch = new XHRPromise()
        this.activeFetch.send({ url: feedEndpoint })
            .then(response => (new window.DOMParser()).parseFromString(response.responseText, 'text/xml'))
            .then((data) => {
                const regions = data.getElementsByTagName('regionTotals')[0]
                const description = data.getElementsByTagName('description')[0]
                const pageTitle = description.getAttribute('pageTitle')
                const sortOrder = data.getElementsByTagName('SortOrder')[0]
                this.sorting.updateSortControl(sortOrder)
                const params = Object.assign({
                    totalPages: parseInt(description.getAttribute('maxPages'), 10),
                    totalCount: parseInt(description.getAttribute('propertyCount'), 10),
                    title: description.getAttribute('resultsTitle'),
                    warning: description.getAttribute('warning'),
                    totalWithoutOfferFilter: parseInt(description.getAttribute('totalWithoutOfferFilter'), 10),
                    regionName: description.getAttribute('regionName'),
                }, History.getState().data)

                if (data.getElementsByTagName('location').length > 0) {
                    params.locationName = data.getElementsByTagName('location')[0].getAttribute('name')
                }

                self.ui.updateUI(params)
                self.pages.setNoOfPages(params.totalPages)
                // Breadcrumbs
                self.ui.setBreadcrumbs(data.getElementsByTagName('breadcrumbs')[0])

                // Update region counters
                self.locations.setRegionCounts(regions)

                // toggle header state (i.e. remove FS or location header) if apending results then leave header alone :)
                const headerXML = data.getElementsByTagName('header');
                if (headerXML.length == 0) {
                    if (!preservePageHeader)
                        this.header.hide();
                } else {
                    this.header.update(headerXML[0]);
                }

                // record a page view
                window.dataLayerQueue.push({ event: 'pageView', 'virtualPageURL': History.getState().url.replace(/^.*\/\/[^\/]+/, '') });
                this.tracking.trackSearch(qs, params)

                // Update the list of cottages.
                this.resultsList.innerHTML = ''

                const listItems = data.querySelectorAll('listItem, listAd')

                Array.from(listItems).forEach((item) => {
                    switch (item.tagName) {
                        case 'listItem':
                            const cottageCode = item.getAttribute('code')
                            const itemCode = item.getAttribute('type') + cottageCode
                            const position = parseInt(item.getAttribute('positionInResults'), 10)
                            const tempWrapper = document.createElement('div')
                            tempWrapper.innerHTML = he.decode(item.getAttribute('html'))
                            const resultItemEl = tempWrapper.children[0]
                            this.resultsList.appendChild(resultItemEl)

                            // if its a complex listing, init the slider
                            if (new Helpers(resultItemEl).hasClass('results-complex')) {
                                new Slider(resultItemEl.querySelector('.slider'), {
                                    navTemplate: '<a href="#"><svg width="28" height="27" viewBox="0 0 28 27" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M12.0006 0L0.62545 11.1186L2.59462 13.0741L12.0006 3.8808L26.0308 17.595L28 15.6395L12.0006 0Z" /><path d="M20.9976 27V15.4287L23.7939 18.251V27H20.9976Z"/></svg><span>{index}</span></a>',
                                }).activate()
                            }
                            this.resultsItemObserver.observe(resultItemEl)
                            // track impression through GTM
                            //   window.dataLayerQueue.push({ event: 'cottageImpression', cottageCode, position })
                            break;

                        case 'listAd':
                            if (item.getAttribute('html') !== '') {
                                const tempWrapper = document.createElement('div')
                                tempWrapper.innerHTML = he.decode(item.getAttribute('html'))
                                const adEl = tempWrapper.children[0]
                                this.resultsList.appendChild(adEl)
                            }
                            break;
                    }
                })

                // set the document title for the browser tab
                if (updateMetaTitle)
                    document.title = pageTitle
                // highlight the shortlisted cottages
                window.sl.HighlightShortlistedCottages()
                // clear the ajax fetch indicator
                self.activeFetch = -1

                if (document.getElementsByClassName('short-break-links').length > 0) {
                    if (document.getElementsByClassName('short-break-links')[0].parentElement.getAttribute('aria-hidden') === 'false') {
                        // if region not west then set then
                        const currentRegion = self.locations.getRegionForFeaturedSearchLinks()
                        if (currentRegion !== null) {
                            const buttons = document.querySelectorAll('.short-break-links__link')
                            if (currentRegion === '') {
                                Array.from(buttons).forEach((button) => {
                                    const featuredSearchCode = button.getAttribute('data-fsCode')
                                    button.setAttribute('href', `/${featuredSearchCode}.html`)
                                })
                            } else {
                                Array.from(buttons).forEach((button) => {
                                    const featuredSearchCode = button.getAttribute('data-fsCode')
                                    button.setAttribute('href', `/${featuredSearchCode}${currentRegion}`)
                                })
                            }
                        }
                    }
                }

                if (preservePageDescription && document.querySelector('.results-lead__text')) {
                    document.querySelector('.results-lead__text').setAttribute('aria-hidden', false);
                }


                // final call to ensure UI <-> state consistency 
                this.updateState(url);
            }).catch((error) => {
                // TODO: UI/UX for error handling
                if (error.reason !== 'abort') {
                    console.log(error)
                }
            })
    }

    showMore() {
        // if there's a current fetch, escape now
        if (this.activeFetch !== -1) return
        const self = this
        // increment max page
        this.pages.showMore()

        // fetch updated qs
        const values = this.getNonDefaultValues()
        let url = `/results.aspx?${this.buildQueryString(values)}`
        // for the sake of "show more", pMin should be equal to pMax
        // as we're only fetching the delta
        values.pMin = values.pMax
        let feedEndpoint = `/feeds/resultsFeedHtml.aspx?rpp=10&${this.buildQueryString(values)}`

        if (this.isCustomizedSearch()) {
            url += '&search=Y'
            feedEndpoint += '&search=Y'
        }

        // naff workaround for history.js - set flag to prevent ajax fetches
        // when we're just trying to modify current state
        this.stateAction = 'replace'
        History.replaceState(this.getValues(), document.title, url)
        this.stateAction = ''
        // init loading anim
        this.ui.showMoreLoading(true)
        // ajax fetch new results
        this.activeFetch = new XHRPromise()
        this.activeFetch.send({ url: feedEndpoint })
            .then(response => (new window.DOMParser()).parseFromString(response.responseText, 'text/xml'))
            .then((data) => {
                const description = data.getElementsByTagName('description')[0]

                const params = Object.assign({
                    totalPages: parseInt(description.getAttribute('maxPages'), 10),
                    totalCount: parseInt(description.getAttribute('propertyCount'), 10),
                    title: description.getAttribute('resultsTitle'),
                    warning: description.getAttribute('warning'),
                    totalWithoutOfferFilter: parseInt(description.getAttribute('totalWithoutOfferFilter'), 10),
                    regionName: description.getAttribute('regionName'),
                }, History.getState().data)

                if (data.getElementsByTagName('location').length > 0) {
                    params.locationName = data.getElementsByTagName('location')[0].getAttribute('name')
                }

                self.ui.updateUI(params)
                self.pages.setNoOfPages(params.totalPages)

                // record a page view
                window.dataLayerQueue.push({ event: 'pageView', 'virtualPageURL': History.getState().url.replace(/^.*\/\/[^\/]+/, '') });

                // Update the list of cottages.
                const listItems = data.querySelectorAll('listItem, listAd')
                Array.from(listItems).forEach((item) => {
                    if (item.tagName === 'listItem') {
                        const cottageCode = item.getAttribute('code')
                        const position = parseInt(item.getAttribute('positionInResults'), 10)
                        // create the element in a temp wrapper div
                        const tempWrapper = document.createElement('div')
                        tempWrapper.innerHTML = he.decode(item.getAttribute('html'))
                        const resultItemEl = tempWrapper.children[0]
                        // append it to the results list
                        this.resultsList.appendChild(resultItemEl)
                        // if its a complex listing, init the slider
                        if (new Helpers(resultItemEl).hasClass('results-complex')) {
                            new Slider(resultItemEl.querySelector('.slider'), {
                                navTemplate: '<a href="#"><svg width="28" height="27" viewBox="0 0 28 27" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M12.0006 0L0.62545 11.1186L2.59462 13.0741L12.0006 3.8808L26.0308 17.595L28 15.6395L12.0006 0Z" /><path d="M20.9976 27V15.4287L23.7939 18.251V27H20.9976Z"/></svg><span>{index}</span></a>',
                            }).activate()
                        }

                        // track impression through GTM
                        window.dataLayerQueue.push({ event: 'cottageImpression', cottageCode, position })

                        //add new logging here
                        this.resultsItemObserver.observe(resultItemEl)

                    }
                    if (item.tagName === 'listAd' && item.getAttribute('html') !== '') {
                        const tempWrapper = document.createElement('div')
                        tempWrapper.innerHTML = he.decode(item.getAttribute('html'))
                        const adEl = tempWrapper.children[0]
                        this.resultsList.appendChild(adEl)
                    }
                })

                // load of "show more" complete, hide the loading element
                this.ui.showMoreLoading(false)
                // highlight the shortlisted cottages
                window.sl.HighlightShortlistedCottages()
                // clear the ajax fetch indicator
                self.activeFetch = -1
            }).catch((error) => {
                // TODO: UI/UX for error handling
                if (error.reason !== 'abort') {
                    console.log(error)
                    this.ui.showMoreLoading(false)
                    this.activeFetch = -1
                }
            })
    }

    // update search controls to reflect current search state in query string
    updateControls(values) {
        this.locations.setValues(values, window.location.pathname)
        this.facets.setValues(values)
        this.party.setValues(values)
        this.dates.setValues(values)
        this.pages.setValues(values)
        this.map.setValues(values)
        this.sorting.setValues(values)
        this.featuredSearchId = values.fsi || 0

        //update share control with current search link
        new Helpers(document.getElementsByClassName('social-share'))
            .setAttributes({ 'data-link': window.location.href.replace('http://', 'https://') })
    }
}


if (searchControl !== undefined) {
    new Results()
}
